import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import {Box, ButtonBase, Stack, SxProps, Theme, Tooltip, Typography} from "@mui/material";
import {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {Reservation, ReservationSource} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {getProfileFullName} from "crqlar-react-common/dist/utils/profile.util";
import {DateTime} from "luxon";
import {useState} from "react";
import {useTranslation} from "react-i18next";

interface Props {
    reservation?: Reservation
    /** Ids of the guests on the booking; the dialog knows them before their profiles have loaded. */
    guestIds: string[]
    /** Their loaded profiles, for the app booker's name. */
    guests?: Profile[]
    /** Room around the row, which depends on where the dialog puts it. */
    sx?: SxProps<Theme>
}

// The booking form's optional address fields; the country comes as an ISO code.
const addressOf = (fields: Record<string, string> | undefined, language: string) => {
    if (!fields) return ''
    const code = fields.country?.trim()
    let country: string | undefined = code
    try {
        if (code?.length === 2) country = new Intl.DisplayNames([language], {type: 'region'}).of(code.toUpperCase()) ?? code
    } catch {
        // An unknown code stays as it was entered.
    }
    const cityLine = [fields.zipCode, fields.city].map(part => part?.trim()).filter(Boolean).join(' ')
    return [fields.streetName?.trim(), [cityLine, country].filter(Boolean).join(', ')].filter(Boolean).join('\n')
}

/*
    What the guest typed into the online booking form (website or guest app), on every booking that has it. Opened
    from the start while no guest is linked, since it is then the only record of who booked; closed otherwise, with
    the entered name in sight. Service and note already sit on the booking, so they are left out.
*/
const EnteredOnline = ({reservation, guestIds, guests, sx}: Props) => {

    const {t, i18n} = useTranslation()
    const [toggled, setToggled] = useState<boolean>()

    const request = reservation?.reservationRequestData
    const hasGuest = guestIds.length > 0
    if (!request) return null

    const enteredName = [request.firstName, request.lastName].map(part => part?.trim()).filter(Boolean).join(' ')
    const rows = [
        {label: t('Name'), value: enteredName},
        {label: t('Email'), value: request.email?.trim()},
        {label: t('Phone'), value: request.phone?.trim()},
        {label: t('Address'), value: addressOf(request.optionalFields, i18n.language)},
    ].filter(row => !!row.value)

    const fromApp = reservation?.source === ReservationSource.GUEST_APP
    const SourceIcon = fromApp ? PhoneIphoneOutlinedIcon : LanguageOutlinedIcon
    const sourceText = fromApp ? t('Booked in the guest app') : t('Booked on the website')

    const booker = fromApp ? guests?.find(guest => guest.id === reservation?.primaryProfileId) : undefined
    const bookedAt = reservation?.insertDate && DateTime.fromJSDate(new Date(reservation.insertDate)).toFormat('dd.MM.yyyy, HH:mm')
    if (bookedAt) {
        rows.push({
            label: t('Booked'),
            value: booker ? `${bookedAt} ${t('by {{name}}', {name: getProfileFullName(booker)})}` : bookedAt
        })
    }

    const open = toggled ?? !hasGuest
    const ChevronIcon = open ? ExpandLessIcon : ExpandMoreIcon

    return (
        <Box sx={[{border: '1px solid', borderColor: 'divider', borderRadius: '8px', flexShrink: 0}, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}>
            <ButtonBase
                onClick={() => setToggled(!open)}
                aria-expanded={open}
                sx={{width: '100%', justifyContent: 'flex-start', gap: '8px', px: '12px', py: '10px', borderRadius: '8px', textAlign: 'left'}}
            >
                <Tooltip title={sourceText} arrow>
                    <SourceIcon aria-label={sourceText} sx={{fontSize: 18, color: 'text.secondary'}}/>
                </Tooltip>
                <Typography component={'span'} sx={{fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap'}}>
                    {t('Entered online')}
                </Typography>
                {/* Closed, the entered name stays in sight; opened, it moves into the rows. */}
                <Typography
                    component={'span'}
                    sx={{flex: 1, minWidth: 0, fontSize: 14, color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}
                >
                    {!open && enteredName ? `· ${enteredName}` : ''}
                </Typography>
                <ChevronIcon sx={{fontSize: 18, color: 'text.secondary'}}/>
            </ButtonBase>

            {open && rows.map(row => (
                <Stack key={row.label} direction={'row'} gap={'12px'} sx={{px: '12px', py: '8px', borderTop: '1px solid', borderColor: 'divider'}}>
                    <Typography sx={{width: 72, flexShrink: 0, fontSize: 14, color: 'text.secondary'}}>{row.label}</Typography>
                    <Typography sx={{minWidth: 0, fontSize: 14, whiteSpace: 'pre-line', overflowWrap: 'anywhere'}}>{row.value}</Typography>
                </Stack>
            ))}
        </Box>
    )
}

export default EnteredOnline;
